import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Users,
  Building2,
  Package,
  BoxesIcon,
  CreditCard,
  Wallet,
  BarChart3,
  HeadphonesIcon,
  Settings,
  LogOut,
  ChevronsUpDown,
  BookOpen,
  History,
  Activity,
  Sun,
  Moon,
  Monitor,
  Check,
  UserCircle,
  FileText,
  Mail,
  Plug,
  Receipt,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  MoreHorizontal,
  Key,
  Webhook,
  TestTube,
  Code2,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth-context";
import { useFeatureFlags } from "@/lib/feature-flags-context";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/theme-provider";

// Admin roles - each nav item specifies which roles can see it
type AdminRole = "SUPER" | "ORGANIZATION" | "BUSINESS_UNIT" | "FINANCE";

const ALL_ROLES: AdminRole[] = ["SUPER", "ORGANIZATION", "BUSINESS_UNIT", "FINANCE"];

interface NavItemDef {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: AdminRole[];
}

// Organization section
const organizationItems: NavItemDef[] = [
  { title: "Organizations", url: "/organizations", icon: Users, roles: ["SUPER"] },
];

// Card Operations section
const cardOperationsItems: NavItemDef[] = [
  { title: "Products", url: "/products", icon: Package, roles: ["SUPER", "ORGANIZATION"] },
  { title: "Customers", url: "/customers", icon: Users, roles: ["SUPER", "ORGANIZATION", "BUSINESS_UNIT"] },
  { title: "Wallets", url: "/wallets", icon: Wallet, roles: ["SUPER", "ORGANIZATION", "BUSINESS_UNIT"] },
  { title: "Cards", url: "/cards", icon: CreditCard, roles: ["SUPER", "ORGANIZATION", "BUSINESS_UNIT"] },
  { title: "Inventory", url: "/inventory", icon: BoxesIcon, roles: ["SUPER", "ORGANIZATION"] },
];

// Operations section
const operationsItems: NavItemDef[] = [
  { title: "Transactions", url: "/transactions", icon: Receipt, roles: ALL_ROLES },
  { title: "Analytics", url: "/analytics", icon: TrendingUp, roles: ["SUPER", "ORGANIZATION"] },
  { title: "Support", url: "/support", icon: HeadphonesIcon, roles: ["SUPER", "ORGANIZATION", "BUSINESS_UNIT"] },
  { title: "Email Templates", url: "/email-templates", icon: Mail, roles: ["SUPER"] },
];

// Developer section
const developerItems: NavItemDef[] = [
  { title: "API Keys", url: "/developer/api-keys", icon: Key, roles: ["SUPER"] },
  { title: "Webhooks", url: "/developer/webhooks", icon: Webhook, roles: ["SUPER"] },
  { title: "API Usage", url: "/developer/usage", icon: BarChart3, roles: ["SUPER"] },
  { title: "Security Tests", url: "/developer/security-tests", icon: TestTube, roles: ["SUPER"] },
  { title: "API Docs", url: "/developer/docs", icon: BookOpen, roles: ["SUPER"] },
];

// More section (collapsible)
const moreItems: NavItemDef[] = [
  { title: "Reports", url: "/reports", icon: BarChart3, roles: ALL_ROLES },
  { title: "Integrations", url: "/integrations", icon: Plug, roles: ["SUPER"] },
  { title: "Platform Analytics", url: "/platform-analytics", icon: Activity, roles: ["SUPER"] },
  { title: "Audit Log", url: "/audit-log", icon: FileText, roles: ["SUPER"] },
  { title: "Docs", url: "/docs", icon: BookOpen, roles: ["SUPER", "ORGANIZATION"] },
  { title: "Changelog", url: "/changelog", icon: History, roles: ["SUPER"] },
  { title: "Privacy Policy", url: "/privacy-policy", icon: ShieldCheck, roles: ALL_ROLES },
];

// Settings visibility per role
const settingsRoles: AdminRole[] = ALL_ROLES;

function filterByRole(items: NavItemDef[], role: AdminRole): NavItemDef[] {
  return items.filter((item) => item.roles.includes(role));
}

// Role-based visibility — filters each section's items by the user's role
function getVisibleNavItems(role: string) {
  const adminRole = role as AdminRole;
  if (!ALL_ROLES.includes(adminRole)) {
    return {
      organization: [] as NavItemDef[],
      cardOperations: [] as NavItemDef[],
      operations: [] as NavItemDef[],
      developer: [] as NavItemDef[],
      more: [] as NavItemDef[],
      showSettings: false,
    };
  }

  return {
    organization: filterByRole(organizationItems, adminRole),
    cardOperations: filterByRole(cardOperationsItems, adminRole),
    operations: filterByRole(operationsItems, adminRole),
    developer: filterByRole(developerItems, adminRole),
    more: filterByRole(moreItems, adminRole),
    showSettings: settingsRoles.includes(adminRole),
  };
}

interface NavItemProps {
  item: NavItemDef | { title: string; url: string; icon: React.ComponentType<{ className?: string }> };
  location: string;
}

interface SectionLabelProps {
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}

function SectionLabel({ children, icon: Icon }: SectionLabelProps) {
  const { state } = useSidebar();

  if (state === "collapsed") {
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="flex items-center justify-center h-7 w-full">
            <div className="h-px w-6 bg-sidebar-border" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          <span className="text-xs font-medium">{children}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <SidebarGroupLabel className="text-white/50 text-xs font-medium px-2">
      {children}
    </SidebarGroupLabel>
  );
}

function NavItem({ item, location }: NavItemProps) {
  const isActive = location === item.url || location.startsWith(item.url + "/") || location.startsWith(item.url + "?");
  const { state } = useSidebar();

  return (
    <SidebarMenuItem>
      <Tooltip delayDuration={state === "collapsed" ? 200 : 800}>
        <TooltipTrigger asChild>
          <SidebarMenuButton
            asChild
            isActive={isActive}
            className={cn(
              "relative",
              isActive && [
                "bg-white/10",
                "before:absolute before:left-0 before:top-0 before:bottom-0",
                "before:w-[2px] before:bg-white",
                "before:rounded-r-sm"
              ]
            )}
          >
            <Link href={item.url}>
              <item.icon className={cn(
                "h-4 w-4 shrink-0",
                isActive ? "text-white" : "text-white/60"
              )} />
              <span className={cn(
                isActive ? "font-semibold text-white" : "text-white/80"
              )}>
                {item.title}
              </span>
            </Link>
          </SidebarMenuButton>
        </TooltipTrigger>
        {state === "collapsed" && (
          <TooltipContent
            side="right"
            className="font-medium"
            sideOffset={8}
          >
            {item.title}
          </TooltipContent>
        )}
      </Tooltip>
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { flags } = useFeatureFlags();
  const isOnMorePage = moreItems.some(
    (item) => location === item.url || location.startsWith(item.url + "/")
  );
  const [isMoreOpen, setIsMoreOpen] = useState(isOnMorePage);

  useEffect(() => {
    if (isOnMorePage) {
      setIsMoreOpen(true);
    }
  }, [isOnMorePage]);

  const visibleNav = getVisibleNavItems(user?.role || "");

  // Filter out Inventory when feature flag is disabled
  if (!flags.inventory) {
    visibleNav.cardOperations = visibleNav.cardOperations.filter(
      (item) => item.title !== "Inventory"
    );
  }

  const handleLogout = async () => {
    await logout();
  };

  const themeOptions = [
    { value: "light" as const, label: "Light", icon: Sun },
    { value: "dark" as const, label: "Dark", icon: Moon },
    { value: "system" as const, label: "System", icon: Monitor },
  ];

  const CurrentThemeIcon = theme === "dark" ? Moon : theme === "system" ? Monitor : Sun;

  const isSettingsActive = location.startsWith("/settings") && !location.includes("tab=integrations");

  const { state: sidebarState, toggleSidebar } = useSidebar();

  return (
    <Sidebar collapsible="icon" className="flex flex-col">
      {/* Logo Section */}
      <SidebarHeader className={cn(
        "p-4 shrink-0",
        sidebarState === "collapsed" && "p-3"
      )}>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Link
              href="/"
              className={cn(
                "flex items-center rounded-lg p-2 hover:bg-sidebar-accent transition-colors",
                sidebarState === "collapsed" && "justify-center"
              )}
            >
              <img
                src="/vedalogic-logo.svg"
                alt="VedaLogic"
                className={cn(
                  "object-contain shrink-0",
                  sidebarState === "collapsed" ? "h-8 w-8" : "h-10 w-auto"
                )}
              />
            </Link>
          </TooltipTrigger>
          {sidebarState === "collapsed" && (
            <TooltipContent side="right" sideOffset={8}>
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-sm">VedaLogic</span>
                <span className="text-xs text-muted-foreground">Card Management</span>
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </SidebarHeader>

      <SidebarContent className="flex-1 overflow-y-auto sidebar-scrollbar">
        {/* Dashboard & Organizations */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavItem
                item={{ title: "Dashboard", url: "/", icon: LayoutDashboard }}
                location={location === "/" ? "/" : location}
              />
              {visibleNav.organization.map((item) => (
                <NavItem key={item.url} item={item} location={location} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Card Operations */}
        {visibleNav.cardOperations.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleNav.cardOperations.map((item) => (
                    <NavItem key={item.url} item={item} location={location} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Operations */}
        {visibleNav.operations.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleNav.operations.map((item) => (
                    <NavItem key={item.url} item={item} location={location} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Developer */}
        {visibleNav.developer.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SectionLabel icon={Code2}>Developer</SectionLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleNav.developer.map((item) => (
                    <NavItem key={item.url} item={item} location={location} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Settings */}
        {visibleNav.showSettings && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <NavItem
                    item={{ title: "Settings", url: "/settings", icon: Settings }}
                    location={isSettingsActive ? "/settings" : location}
                  />
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* More (Collapsible) */}
        {visibleNav.more.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <Collapsible open={isMoreOpen} onOpenChange={setIsMoreOpen}>
                    <SidebarMenuItem>
                      <Tooltip delayDuration={sidebarState === "collapsed" ? 200 : 800}>
                        <TooltipTrigger asChild>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton className="w-full">
                              <MoreHorizontal className="h-4 w-4 shrink-0 text-white/60" />
                              <span className="text-white/80">More</span>
                              <ChevronDown className={cn(
                                "ml-auto h-4 w-4 shrink-0 text-white/60 transition-transform",
                                isMoreOpen && "rotate-180"
                              )} />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                        </TooltipTrigger>
                        {sidebarState === "collapsed" && (
                          <TooltipContent side="right" sideOffset={8}>
                            More
                          </TooltipContent>
                        )}
                      </Tooltip>
                      <CollapsibleContent className="mt-1 space-y-1 pl-4">
                        {visibleNav.more.map((item) => (
                          <NavItem key={item.url} item={item} location={location} />
                        ))}
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      {/* Bottom Section - Always Visible */}
      <div className="shrink-0 mt-auto">
        {/* Collapse Button */}
        <div className="px-2 pb-2">
          <button
            onClick={toggleSidebar}
            className={cn(
              "flex items-center gap-2 w-full rounded-lg p-2 text-white/80 hover:text-white hover:bg-sidebar-accent transition-colors",
              sidebarState === "collapsed" && "justify-center"
            )}
          >
            {sidebarState === "collapsed" ? (
              <PanelLeftOpen className="h-4 w-4 shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">Collapse menu</span>
              </>
            )}
          </button>
        </div>

        {/* Version & User Profile Footer */}
        <SidebarFooter className="p-1.5 border-t">
          {sidebarState !== "collapsed" && (
            <div className="px-2 pt-1 pb-0.5 flex items-center gap-2">
              <span className="text-[10px] text-white/30 font-mono">v1.0.0-rc.3</span>
              <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-emerald-400/60 hover:text-emerald-400 transition-colors">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Status
              </a>
            </div>
          )}
        <DropdownMenu>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "flex items-center w-full rounded-lg px-2 py-1.5 hover:bg-white/10 transition-colors text-left",
                    sidebarState === "collapsed" && "justify-center"
                  )}
                >
                  {sidebarState !== "collapsed" && (
                    <>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-medium text-white truncate">
                          {user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user?.email || "Loading..."}
                        </span>
                        <span className="text-[10px] text-white/50 truncate">
                          {user?.role === "SUPER" ? "Super Admin" : user?.role?.replace("_", " ") || ""}
                        </span>
                      </div>
                      <ChevronsUpDown className="h-3.5 w-3.5 text-white/60 shrink-0 ml-2" />
                    </>
                  )}
                  {sidebarState === "collapsed" && (
                    <UserCircle className="h-4 w-4 text-white/60" />
                  )}
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            {sidebarState === "collapsed" && (
              <TooltipContent side="right" sideOffset={8}>
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium text-sm">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email || "User"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {user?.role === "SUPER" ? "Super Admin" : user?.role?.replace("_", " ")}
                  </span>
                </div>
              </TooltipContent>
            )}
          </Tooltip>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 p-0 overflow-hidden"
          >
            {/* User Profile Header */}
            <div className="px-3 py-2.5 border-b bg-muted/50">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground text-sm truncate">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email || "User"}
                  </span>
                  {user?.role === "SUPER" && (
                    <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-semibold rounded bg-primary/10 text-primary">
                      ADMIN
                    </span>
                  )}
                </div>
                {user?.firstName && user?.lastName && (
                  <span className="text-xs text-muted-foreground truncate">
                    {user?.email || ""}
                  </span>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-1.5">
              {/* Profile & Settings */}
              <DropdownMenuItem
                onClick={() => setLocation("/settings")}
                className="cursor-pointer gap-2.5 py-2 px-2.5 rounded-md"
              >
                <div className="flex items-center justify-center h-7 w-7 rounded-md bg-primary/10">
                  <UserCircle className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="text-sm">Profile & Settings</span>
              </DropdownMenuItem>

              {/* Theme Submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer gap-2.5 py-2 px-2.5 rounded-md">
                  <div className="flex items-center justify-center h-7 w-7 rounded-md bg-primary/10">
                    <CurrentThemeIcon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="text-sm flex-1">Theme</span>
                  <span className="text-xs text-muted-foreground capitalize">{theme}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="min-w-[140px] p-1.5">
                    {themeOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => setTheme(option.value)}
                        className={cn(
                          "cursor-pointer gap-2.5 py-2 px-2.5 rounded-md",
                          theme === option.value && "bg-primary/10"
                        )}
                      >
                        <option.icon className={cn(
                          "h-4 w-4",
                          theme === option.value ? "text-primary" : "text-muted-foreground"
                        )} />
                        <span className="flex-1 text-sm">{option.label}</span>
                        {theme === option.value && (
                          <Check className="h-3.5 w-3.5 text-primary" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <DropdownMenuSeparator className="my-1.5" />

              {/* Sign Out */}
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer gap-2.5 py-2 px-2.5 rounded-md text-red-500 focus:text-red-500 focus:bg-red-500/10"
              >
                <div className="flex items-center justify-center h-7 w-7 rounded-md bg-red-500/10">
                  <LogOut className="h-3.5 w-3.5" />
                </div>
                <span className="text-sm">Sign Out</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
      </div>
    </Sidebar>
  );
}
